import React, { useState, useEffect, useCallback } from 'react';
import { getPatients, createPatient, updatePatient, deletePatient } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Search, Phone, Mail, User, Pencil, Trash2, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PHONE_DIGITS_ONLY = /^\d*$/;
const PHONE_LENGTH = 10;

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    paternal_surname: '',
    maternal_surname: '',
    whatsapp: '',
    email: '',
    soap_note_frequency: 3,
    consent_notifications: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const fetchPatients = useCallback(async () => {
    try {
      const response = await getPatients(searchTerm);
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setPatients(Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar clientes'));
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleOpenDrawer = (patient = null) => {
    setSelectedPatient(patient);
    if (patient) {
      setFormData({
        first_name: patient.first_name,
        paternal_surname: patient.paternal_surname,
        maternal_surname: patient.maternal_surname || '',
        whatsapp: patient.whatsapp,
        email: patient.email || '',
        soap_note_frequency: patient.soap_note_frequency ?? 3,
        consent_notifications: patient.consent_notifications,
      });
    } else {
      setFormData({
        first_name: '',
        paternal_surname: '',
        maternal_surname: '',
        whatsapp: '',
        email: '',
        soap_note_frequency: 3,
        consent_notifications: true,
      });
    }
    setDrawerOpen(true);
  };

  const handlePhoneChange = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, PHONE_LENGTH);
    if (PHONE_DIGITS_ONLY.test(digits) || digits === '') {
      setFormData({ ...formData, whatsapp: digits });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phoneDigits = (formData.whatsapp || '').replace(/\D/g, '');
    const hasPhone = phoneDigits.length === PHONE_LENGTH;
    const hasEmail = (formData.email || '').trim() !== '';
    if (!hasPhone && !hasEmail) {
      toast.error('Debe ingresar correo o teléfono (al menos uno).');
      return;
    }
    if (hasPhone && phoneDigits.length !== PHONE_LENGTH) {
      toast.error('El número debe tener 10 dígitos');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        first_name: formData.first_name,
        paternal_surname: formData.paternal_surname,
        maternal_surname: formData.maternal_surname || null,
        whatsapp: hasPhone ? phoneDigits : null,
        email: hasEmail ? (formData.email || '').trim() || null : null,
        soap_note_frequency: formData.soap_note_frequency ? Number(formData.soap_note_frequency) : undefined,
        consent_notifications: formData.consent_notifications,
      };

      if (selectedPatient) {
        await updatePatient(selectedPatient.id, payload);
        toast.success('Cliente actualizado');
        setDrawerOpen(false);
      } else {
        await createPatient(payload);
        toast.success('Cliente registrado');
        setDrawerOpen(false);
      }
      fetchPatients();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    try {
      await deletePatient(id);
      toast.success('Cliente eliminado');
      fetchPatients();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Clientes</h1>
          <p className="text-[#5D5C61] mt-1">Gestiona los registros de clientes</p>
        </div>
        <Button
          className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
          onClick={() => handleOpenDrawer()}
          data-testid="patients-new-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cliente
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8D99AE]" />
          <Input
            placeholder="Buscar por nombre o WhatsApp..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 bg-white border-slate-200"
            data-testid="patients-search-input"
          />
        </div>
      </div>

      {/* Patients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {patients.map((patient) => (
          <Card
            key={patient.id}
            className="bg-white border-slate-100 hover:shadow-md hover:border-[#335C67]/20 transition-all cursor-pointer"
            data-testid={`patient-card-${patient.id}`}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#335C67] flex items-center justify-center text-white font-semibold text-lg">
                    {patient.first_name.charAt(0)}{patient.paternal_surname.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-lg text-[#2B2D42]">{patient.full_name}</CardTitle>
                    {patient.otp_verified && (
                      <span className="text-xs text-[#588157] font-medium">✓ Verificado</span>
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#5D5C61]">
                  <Phone className="w-4 h-4" />
                  <span>{patient.whatsapp}</span>
                </div>
                {patient.email && (
                  <div className="flex items-center gap-2 text-[#5D5C61]">
                    <Mail className="w-4 h-4" />
                    <span>{patient.email}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 hover:bg-[#F4F1EA]"
                  onClick={() => handleOpenDrawer(patient)}
                  data-testid={`patient-edit-${patient.id}`}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 hover:bg-[#F4F1EA]"
                  onClick={() => navigate(`/expedientes/${patient.id}`)}
                  data-testid={`patient-record-${patient.id}`}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Expediente
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
                  onClick={() => handleDelete(patient.id)}
                  data-testid={`patient-delete-${patient.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {patients.length === 0 && (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[#2B2D42]">No hay clientes</h3>
          <p className="text-[#5D5C61] mt-1">Registra tu primer cliente para comenzar</p>
        </div>
      )}

      {/* Patient Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="patient-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">
              {selectedPatient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </SheetTitle>
            <SheetDescription>
              {selectedPatient ? 'Modifica los datos del cliente' : 'Registra un nuevo cliente'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#8D99AE]" />
                Nombre(s) *
              </Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="Juan Carlos"
                data-testid="patient-first-name-input"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Apellido Paterno *</Label>
                <Input
                  value={formData.paternal_surname}
                  onChange={(e) => setFormData({ ...formData, paternal_surname: e.target.value })}
                  placeholder="Pérez"
                  data-testid="patient-paternal-surname-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Apellido Materno</Label>
                <Input
                  value={formData.maternal_surname}
                  onChange={(e) => setFormData({ ...formData, maternal_surname: e.target.value })}
                  placeholder="García"
                  data-testid="patient-maternal-surname-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#8D99AE]" />
                Teléfono (10 dígitos, México)
              </Label>
              <Input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                value={formData.whatsapp}
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="5512345678"
                maxLength={PHONE_LENGTH}
                data-testid="patient-whatsapp-input"
              />
              {formData.whatsapp.length > 0 && formData.whatsapp.length !== PHONE_LENGTH && (
                <p className="text-xs text-[#9E2A2B]">El número debe tener 10 dígitos</p>
              )}
              <p className="text-xs text-[#8D99AE]">Correo o teléfono obligatorio (al menos uno). Cada uno debe ser único.</p>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#8D99AE]" />
                Correo electrónico
              </Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="correo@ejemplo.com"
                data-testid="patient-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Frecuencia de nota de seguimiento</Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={formData.soap_note_frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    soap_note_frequency: e.target.value ? Number(e.target.value) : '',
                  })
                }
                placeholder="3"
              />
              <p className="text-xs text-[#8D99AE]">
                Indica cada cuántas citas este cliente requiere una nota de seguimiento. Ejemplo: 1 = cada cita, 3 = cada 3 citas.
              </p>
            </div>

            <div className="flex items-center justify-between py-4 px-4 bg-[#F4F1EA] rounded-lg">
              <div>
                <Label className="font-medium">Notificaciones</Label>
                <p className="text-sm text-[#5D5C61]">Recibir recordatorios de citas</p>
              </div>
              <Switch
                checked={formData.consent_notifications}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, consent_notifications: checked })
                }
                data-testid="patient-notifications-switch"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              disabled={isLoading || ((formData.whatsapp || '').replace(/\D/g, '').length !== PHONE_LENGTH && !(formData.email || '').trim())}
              data-testid="patient-save-btn"
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
